import type { HTMLAttributes, ReactNode } from 'react';

import {
  CheckmarkCircle,
  InformationCircle,
  WarningCircle,
  X,
} from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { createContentSwitch } from 'src/utils/createContentSwitch';

import { Button } from './Button';

const toastStyles = cva('max-w-320 rounded-sm p-16 text-left shadow-sheet', {
  variants: {
    variant: {
      success: 'bg-green-light',
      error: 'bg-feedback-red-100',
      warning: 'bg-orange-light',
      info: 'bg-pen-blue-light',
    },
  },
});
type ToastProps = VariantProps<typeof toastStyles> &
  HTMLAttributes<HTMLDivElement> & { title?: ReactNode; onClose?: () => void };

export function Toast({
  children,
  title,
  className,
  onClose,
  ...props
}: ToastProps) {
  const variantSwitch = createContentSwitch(props.variant);
  return (
    <div className={toastStyles({ ...props, className })} {...props}>
      <div className="flex w-full items-center gap-8">
        {variantSwitch({
          info: <InformationCircle size={24} />,
          success: <CheckmarkCircle size={24} />,
          // Using same icon for error & warning variants but with color classes applied around it
          error: <WarningCircle size={24} />,
          warning: <WarningCircle size={24} />,
          default: null,
        })}
        <Typography variant="body-md" fontWeight="bold">
          {title}
        </Typography>

        {onClose && (
          <Button
            variant="tertiary-neutral"
            className="ml-auto"
            size="small"
            onClick={onClose}
          >
            <X />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}
